import React, { useEffect, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, FileSpreadsheet, FileImage, FileBox, Download, Loader2 } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";

const FORMATS = [
    { id: "pdf", title: "PDF Report", desc: "Executive Engineering Report with KPIs · findings · recommendations", icon: FileText, ct: "application/pdf" },
    { id: "docx", title: "Word Report", desc: "Editable engineering report — ideal for client deliverables", icon: FileBox, ct: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
    { id: "xlsx", title: "Excel Workbook", desc: "Multi-sheet workbook · Summary · Findings · Recommendations · Artifacts", icon: FileSpreadsheet, ct: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    { id: "pptx", title: "PowerPoint Deck", desc: "Steering-committee deck — cover · KPIs · top findings · recommendations", icon: FileImage, ct: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
];

export default function Reports() {
    const [workspaces, setWorkspaces] = useState([]);
    const [wsId, setWsId] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/workspaces");
                setWorkspaces(data);
                setWsId(data[0]?.id || null);
            } catch (e) { console.warn(e); }
        })();
    }, []);

    const download = async (fmt) => {
        if (!wsId) { toast.error("Choose a workspace first"); return; }
        setDownloading(fmt);
        try {
            const res = await api.get(`/workspaces/${wsId}/report/${fmt}`, { responseType: "blob" });
            const cd = res.headers["content-disposition"] || "";
            const m = /filename="?([^"]+)"?/.exec(cd);
            const filename = m ? m[1] : `UTSAVA_report.${fmt}`;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`${filename} downloaded`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Export failed");
        } finally { setDownloading(null); }
    };

    const ws = workspaces.find((w) => w.id === wsId);

    return (
        <ModuleGate module="reports" label="Executive Reports">
        <div className="space-y-6">
            <Panel
                eyebrow="Executive Reports"
                title="Choose a workspace and a format"
                action={
                    <Select value={wsId || ""} onValueChange={setWsId}>
                        <SelectTrigger data-testid="reports-workspace-select" className="w-[320px] h-8 bg-[var(--bg-surface-2)] border-white/10 text-xs">
                            <SelectValue placeholder="Select workspace" />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                            {workspaces.map((w) => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            >
                {ws ? (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <Mini label="Artifacts" value={ws.artifact_count} />
                        <Mini label="Findings" value={ws.finding_count} />
                        <Mini label="Recommendations" value={ws.recommendation_count} />
                        <Mini label="Risk" value={ws.risk_score?.toFixed?.(1) ?? 0} tone={kpiTone(ws.risk_score, true)} />
                        <Mini label="Confidence" value={ws.confidence_score?.toFixed?.(1) ?? 0} tone={kpiTone(ws.confidence_score)} />
                        <Mini label="Clean Core" value={ws.clean_core_score?.toFixed?.(1) ?? 0} tone={kpiTone(ws.clean_core_score)} />
                    </div>
                ) : (
                    <p className="text-sm text-slate-400">Pick a workspace to generate an executive report.</p>
                )}
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {FORMATS.map((f) => (
                    <div key={f.id} className="panel-elevated p-5 hover:border-cyan-500/30 transition-colors">
                        <div className="flex items-start justify-between">
                            <div className="w-10 h-10 rounded-sm bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                                <f.icon size={18} className="text-cyan-300" />
                            </div>
                            <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-300/80 border border-cyan-500/20 px-2 py-0.5 rounded-sm">.{f.id}</span>
                        </div>
                        <h3 className="font-display text-lg text-white mt-3">{f.title}</h3>
                        <p className="text-xs text-slate-400 mt-1 min-h-[2.5rem]">{f.desc}</p>
                        <button
                            data-testid={`reports-download-${f.id}`}
                            onClick={() => download(f.id)}
                            disabled={!wsId || downloading === f.id}
                            className="btn-primary w-full mt-4 flex items-center justify-center gap-1.5 disabled:opacity-50"
                        >
                            {downloading === f.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                            {downloading === f.id ? "Generating…" : "Download"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
        </ModuleGate>
    );
}

/** Semantic KPI colour: high is good unless `inverted` (Risk). */
const kpiTone = (v, inverted = false) => {
    if (v === null || v === undefined) return "slate";
    const good = inverted ? v <= 30 : v >= 75;
    const mid = inverted ? v <= 60 : v >= 45;
    return good ? "green" : mid ? "amber" : "rose";
};

function Mini({ label, value, tone = "slate" }) {
    const colorMap = { slate: "text-white", rose: "text-rose-400", amber: "text-amber-400", cyan: "text-cyan-300", green: "text-emerald-400" };
    return (
        <div className="panel px-3 py-3 text-center">
            <div className={`font-display text-xl ${colorMap[tone]}`}>{value}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-1">{label}</div>
        </div>
    );
}
